import {Injectable} from '@angular/core';

/**
 * A registered input that can reconcile its DOM value into its form control.
 * Declared here rather than importing the directive so the directive can depend
 * on the registry without a circular import.
 */
export interface SyncableInput {
    sync(): void;
}

/**
 * Tracks every live input guarded by FormValueSyncDirective so a single
 * `syncAll()` can reconcile the whole page immediately before the value of a
 * form is read.
 *
 * The directive already syncs on input / change / blur / autofill, so this is
 * the second layer: it covers a field that was filled programmatically, never
 * focused, and never emitted an event. Without it such a field reaches the
 * payload as null while the browser visibly shows text in the box.
 */
@Injectable({providedIn: 'root'})
export class FormValueSyncRegistry {
    private readonly _inputs = new Set<SyncableInput>();

    register(input: SyncableInput): void {
        this._inputs.add(input);
    }

    unregister(input: SyncableInput): void {
        this._inputs.delete(input);
    }

    /**
     * Reconciles every registered input. Call before reading a form's value —
     * on submit and before advancing a wizard stage.
     *
     * A single misbehaving element must never be able to abort the sweep and
     * block a booking, so failures are logged and skipped.
     */
    syncAll(): void {
        this._inputs.forEach((input: SyncableInput) => {
            try {
                input.sync();
            } catch (error) {
                console.error('Failed to sync input value', error);
            }
        });
    }
}
